<template>
    <div class="workstream-status-tab">
        <div
            aria-labelledby="headingId"
            class="header"
        >
            <h3 id="headingId">
                {{ capitalize(state) }} Workstreams
            </h3>
            <al-dropdown
                variant="wfm"
                :disabled="!(hasEnabledSelections || hasDisabledSelections)"
            >
                <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
                <div slot="label">
                    Actions
                </div>
                <al-dropdown-item
                    @click="enableSelected"
                    v-if="hasDisabledSelections"
                >
                    Enable selected
                </al-dropdown-item>
                <al-dropdown-item
                    @click="disableSelected"
                    v-if="hasEnabledSelections"
                >
                    Disable selected
                </al-dropdown-item>
            </al-dropdown>
        </div>
        <ag-grid-vue
            id="status-grid"
            class="ag-theme-alpine grid"
            :column-defs="columns"
            v-model="rows"
            :dom-layout="domLayout"
            :modules="modules"
            :row-selection="{ mode: 'multiRow', enableClickSelection: true }"
            :column-types="columnTypes"
            :loading="isLoading"
            :suppress-scroll-on-new-data="true"
            :get-row-id="getRowId"
            :pagination="true"
            :pagination-page-size="10"
            :pagination-page-size-selector="[10, 20, 50, 100]"
            :auto-size-strategy="autoSizeStrategy"
            :initial-state="initialGridState"
            @grid-ready="onGridReady"
            @selection-changed="onGridSelectionChanged"
            @filter-changed="onGridFilterChanged"
        />
    </div>
</template>

<script setup lang="ts">
import {
    capitalize, ref, watch
} from 'vue';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
    GetRowIdParams, GridApi, GridReadyEvent, Module, SelectionChangedEvent
} from '@ag-grid-community/core';
import { WorkstreamState } from '@/pages/OperationsAdministration/types/WorkstreamState';

import { updateGridData } from '@/pages/OperationsAdministration/utils/updateGridData';
import { deselectAllRows } from '@/pages/OperationsAdministration/utils/deselectAllRows';
import { updateState, WorkstreamDefinitionOperationConfigGridRow }
    from '@/pages/OperationsAdministration/types/WorkstreamDefinitionOperationConfigGridRow';
import { useWorkstreamConfigGridData } from '@/pages/OperationsAdministration/composables/useWorkstreamConfigGridData';
import { useAutoResizingGrid } from '@/pages/OperationsAdministration/composables/useAutoResizingGrid';
import { usePersistentFilter } from '@/pages/OperationsAdministration/composables/usePersistentFilter';

type GridRow = WorkstreamDefinitionOperationConfigGridRow;

const changedRows = defineModel<GridRow[]>({ required: true });
const props = defineProps<{
    state: WorkstreamState;
    data: GridRow[];
    isLoading: boolean;
}>();

// ---- State tracking ----
const hasEnabledSelections = ref(false);
const hasDisabledSelections = ref(false);

// Grid setup
const gridApi = ref<GridApi<GridRow>>();

const { rows, columns, columnTypes } = useWorkstreamConfigGridData(props);
const { initialGridState, onGridFilterChanged } = usePersistentFilter(gridApi);
useAutoResizingGrid(rows, gridApi, 'status-grid');

const domLayout = 'autoHeight';
const modules = [ClientSideRowModelModule] as Module[];
const autoSizeStrategy = { type: 'fitCellContents' };

const arrayContentsEqual = <T, >(left: T[], right: T[]) =>
    left.length === right.length && left.every((v, i) => v === right[i]);

watch(
    () => rows.value,
    (val, prev) => {
        if (arrayContentsEqual(val, prev)) return;

        const newChanges = rows.value.filter((r) => r.isDirty);
        if (!arrayContentsEqual(newChanges, changedRows.value)) {
            changedRows.value = newChanges;
        }
    }
);

watch(
    changedRows,
    (val, prev) => {
        if (arrayContentsEqual(val, prev)) return;

        const newChanges: GridRow[] = [];
        gridApi.value?.forEachNode((node) => {
            const data = node.data;
            if (data) {
                const id = data.workstreamDefinitionId;
                const change = val.find((r: GridRow) => r.workstreamDefinitionId === id);
                if (change) {
                    newChanges.push(change);
                } else if (data.isDirty) {
                    const originalData = props.data.find((r: GridRow) => r.workstreamDefinitionId === id);
                    if (originalData) {
                        newChanges.push(originalData);
                    }
                }
            }
        });
        if (newChanges.length) {
            updateGridData(gridApi.value, newChanges);
        }
    });

function updateSelections(api: GridApi<GridRow> | undefined) {
    const gridHasSelections: Record<WorkstreamState, boolean> = { enabled: false, disabled: false };

    api?.forEachNode((node) => {
        const data = node.data;
        if (data) {
            const isRowSelected = node.isSelected() || false;
            gridHasSelections[data.state] ||= isRowSelected;
        }
    });

    hasEnabledSelections.value = gridHasSelections[WorkstreamState.enabled];
    hasDisabledSelections.value = gridHasSelections[WorkstreamState.disabled];
}

// Grid events
const onGridReady = (event: GridReadyEvent) => {
    gridApi.value = event.api;
};

const onGridSelectionChanged = (event: SelectionChangedEvent<GridRow>) => updateSelections(event.api);
const getRowId = (params: GetRowIdParams<GridRow>) => params.data.workstreamDefinitionId.toString();

// Actions dropdown events
const enableSelected = () => updateSelected(WorkstreamState.enabled);
const disableSelected = () => updateSelected(WorkstreamState.disabled);

function updateSelected(state: WorkstreamState) {
    const updates = gridApi.value?.getSelectedRows()?.map(
        (originalRow) => updateState(originalRow, state) as GridRow
    );
    updateGridData(gridApi.value, updates);
    deselectAllRows(gridApi.value);
}
</script>

<style scoped lang="scss">
@use '@/styles/commonPageStyles.scss';
.workstream-status-tab {
    height: 100%;
    display: flex;
    flex-direction: column;

    .header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        margin-bottom: 10px;
    }

    .grid-header {
        display: flex;
        align-items: center;
    }
}
</style>

<style lang="scss">
// Unscoped styles for ag-grid

#status-grid {
    // The grid loses the pinned flag on the status column if there's not enough room to display it
    min-width: 500px;

    .ag-pinned-right-cols-container .status-cell {
        display: flex;
        justify-content: center;
    }
}
</style>
